import React from 'react';
import { css } from '@emotion/react';
import { styled, Tooltip, tooltipClasses, TooltipProps } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { color } from '@/styles';

const ActivityStatusHintContainer = css`
  display: flex;
  justify-content: flex-end;
`;

const ActivityStatusHintLabel = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 2px;
  width: fit-content;
  margin-right: 8px;
  font-size: 12px;
  color: ${color.dark_gray};
  cursor: help;
  user-select: none;
`;

const TooltipContent = css`
  width: 400px;
  font-size: 12px;
`;

const StatusRow = css`
  display: flex;
  align-items: center;
  gap: 0 4px;
`;

const StatusRows = css`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`;

const StatusColor = (bg: string) => css`
  width: 6px;
  height: 20px;
  border-radius: 2px;
  background: ${bg};
`;

const CustomTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} arrow classes={{ popper: className }} />
))({
  [`& .${tooltipClasses.arrow}`]: {
    color: color.bg_light_green,
  },
  [`& .${tooltipClasses.tooltip}`]: {
    color: color.dark_gray,
    backgroundColor: color.bg_light_green,
    maxWidth: 'none',
    padding: '8px 8px 8px 12px',
  },
});

export const ActivityTrendColorGuideTooltip = React.memo(() => {
  const { t } = useTranslation();
  const StatusItems = [
    { label: t('StandingAndSitting', '立位＆座位'), color: '#FFF4CC' },
    { label: t('SupinePosition', '仰臥位'), color: '#141F80' },
    { label: t('RightSupinePosition', '右側臥位'), color: '#4EC1D0' },
    { label: t('LeftSupinePosition', '左側臥位'), color: '#5855DB' },
    { label: t('Moving(WhileLyingInBed)', '動いている（寝）'), color: '#B092E2' },
    { label: t('Moving(Standing/Sitting)', '動いている（立・座）'), color: '#FF7152' },
    { label: t('PronePosition', 'うつ伏せ'), color: '#D7F5FF' },
    { label: t('UpsideDown', '上下逆'), color: '#000000' },
  ];

  return (
    <div css={ActivityStatusHintContainer}>
      <CustomTooltip
        placement='bottom-start'
        title={
          <div css={TooltipContent}>
            <div css={StatusRows}>
              {StatusItems.map((item) => (
                <div key={item.label} css={StatusRow}>
                  <span css={StatusColor(item.color)} />
                  <span>{item.label}</span>
                </div>
              ))}
            </div>
            <div>※{t('postureInformationForTheLast30MinutesIsUnavailable', '最大30分の姿勢情報はありません')}</div>
          </div>
        }
      >
        <div css={ActivityStatusHintLabel}>
          <InfoOutlined sx={{ fontSize: 16, color: color.gray }} />
          {t('colorGuide', '色分けについて')}
        </div>
      </CustomTooltip>
    </div>
  );
});
